import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config/api';
import useAuth from '../hooks/useAuth';
import './ResetPassword.css';

const ResetPassword = () => {
  const { token } = useParams();
  const isTokenReset = Boolean(token);
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  useEffect(() => {
    if (!isTokenReset && !authLoading && !user) {
      navigate('/login');
    }
  }, [authLoading, isTokenReset, navigate, user]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setMessage('');
  };

  const validatePasswords = () => {
    if (!formData.newPassword || !formData.confirmPassword || (!isTokenReset && !formData.oldPassword)) {
      setError('Please fill in all fields');
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError(isTokenReset ? 'Passwords do not match' : 'New passwords do not match');
      return false;
    }

    if (formData.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return false;
    }

    if (!isTokenReset && formData.oldPassword === formData.newPassword) {
      setError('New password must be different from old password');
      return false;
    }

    return true;
  };

  const handleTokenReset = async () => {
    const response = await axios.put(
      `${API_BASE}/v1/user/password/reset/${token}`,
      {
        password: formData.newPassword,
        confirmPassword: formData.confirmPassword
      }
    );

    if (response.data.success) {
      setMessage('Password reset successfully! Redirecting to login...');
      setFormData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    }
  };

  const handleAuthenticatedPasswordChange = async () => {
    const response = await axios.put(
      `${API_BASE}/v1/user/password/change`,
      {
        oldPassword: formData.oldPassword,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword
      },
      {
        withCredentials: true
      }
    );

    if (response.data.success) {
      setMessage('Password changed successfully!');
      setFormData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setTimeout(() => {
        navigate('/account');
      }, 2000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validatePasswords()) {
      return;
    }

    setLoading(true);
    try {
      if (isTokenReset) {
        await handleTokenReset();
      } else {
        await handleAuthenticatedPasswordChange();
      }
    } catch (err) {
      setError(err.response?.data?.message || (isTokenReset ? 'Error resetting password' : 'Error changing password'));
    }
    setLoading(false);
  };

  if (!isTokenReset && authLoading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-card">
        <h2>{isTokenReset ? 'Set New Password' : 'Reset Password'}</h2>
        {!isTokenReset && (
          <p className="user-info">
            Logged in as: <strong>{user?.email}</strong>
          </p>
        )}

        <form onSubmit={handleSubmit}>
          {!isTokenReset && (
            <div className="form-group">
              <label htmlFor="oldPassword">Current Password</label>
              <input
                type="password"
                id="oldPassword"
                name="oldPassword"
                value={formData.oldPassword}
                onChange={handleInputChange}
                placeholder="Enter your current password"
                required
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="newPassword">{isTokenReset ? 'New Password' : 'New Password'}</label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleInputChange}
              placeholder="Enter new password"
              minLength="6"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder="Confirm new password"
              minLength="6"
              required
            />
          </div>

          {error && <div className="error-message">{error}</div>}
          {message && <div className="success-message">{message}</div>}

          <button type="submit" disabled={loading} className="submit-btn">
            {loading
              ? (isTokenReset ? 'Resetting Password...' : 'Changing Password...')
              : (isTokenReset ? 'Reset Password' : 'Change Password')}
          </button>

          <div className="form-links">
            <button
              type="button"
              onClick={() => navigate(isTokenReset ? '/login' : '/account')}
              className="link-btn"
            >
              {isTokenReset ? 'Back to Login' : 'Back to Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
